import { useId, useState, type FormEvent } from "react";
import type {
  CustomerAddedBookAddress,
  CustomerAddressCountry,
  CustomerPostcodeAddress,
  CustomerPostcodeLookup,
  CustomerTypedAddressRequest,
} from "@shared-core/data/customer-basket-types";
import {
  addressFormFailure,
  DEFAULT_ADDRESS_COUNTRY_CODE,
  fillFromPremise,
  type AddressFormFailure,
  type AddressFormField,
} from "@shared-core/domain/customer-address-book";
import { Button } from "../../../app/components/primitives/button";
import { Input } from "../../../app/components/primitives/input";
import { Label } from "../../../app/components/primitives/label";
import { Loader2 } from "lucide-react";
import { FindAddressByPostcode } from "./FindAddressByPostcode";

interface Draft {
  label: string;
  line1: string;
  line2: string;
  line3: string;
  town: string;
  county: string;
  postcode: string;
  countryCode: string;
  recipientName: string;
  recipientPhone: string;
  instructions: string;
}

const EMPTY: Draft = {
  label: "",
  line1: "",
  line2: "",
  line3: "",
  town: "",
  county: "",
  postcode: "",
  countryCode: DEFAULT_ADDRESS_COUNTRY_CODE,
  recipientName: "",
  recipientPhone: "",
  instructions: "",
};

// The form for adding a delivery address to the person's own address book
// (docs/features/customer-portal-ordering/21 section 6.6). It only collects and sends what was typed: whether the
// address already existed on the customer's list is the server's business and is never shown (the answer is the
// same either way). Each refusal is bound to the input it names, with a banner that counts them.
//
// Where a postcode lookup is set up it starts with "Find your address" for a United Kingdom address: the person
// chooses a premise and the fields fill in, still editable. "Type it in instead" is always there, and a lookup that
// cannot answer drops straight to the fields with a note saying so: adding an address is never blocked on it.
export function AddAddressForm({
  countries,
  findAddresses,
  onSubmit,
  onAdded,
  onCancel,
}: {
  /**
   * The countries the server offers, one per ISO country with United Kingdom first. The country is chosen from this
   * list and never typed: free text would put names like "uk" or "Britain" into CIS addresses.
   */
  countries: CustomerAddressCountry[];
  /**
   * Looks up the premises at a UK postcode. Null where the server has no address provider set up: the form then offers
   * typing only. The lookup covers the United Kingdom, so the form offers it only while that is the chosen country.
   */
  findAddresses: ((postcode: string) => Promise<CustomerPostcodeLookup>) | null;
  /** Posts the typed address; rejects with the server's refusal, which the form binds to its inputs. */
  onSubmit: (
    request: CustomerTypedAddressRequest,
  ) => Promise<CustomerAddedBookAddress>;
  onAdded: (added: CustomerAddedBookAddress) => void;
  onCancel: () => void;
}) {
  const id = useId();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [step, setStep] = useState<"find" | "details">(
    findAddresses ? "find" : "details",
  );
  // Why the lookup could not answer, shown above the fields it dropped the person to.
  const [lookupNote, setLookupNote] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<AddressFormFailure | null>(null);
  const errors = failure?.fieldErrors ?? {};

  // A chosen premise fills the fields (the person's own name for the address only if they have not typed one) and
  // moves on to them, where everything stays editable.
  function choose(premise: CustomerPostcodeAddress) {
    const fill = fillFromPremise(premise);
    setDraft((d) => ({
      ...d,
      label: d.label.trim() ? d.label : (fill.name ?? ""),
      line1: fill.line1,
      line2: fill.line2,
      line3: fill.line3,
      town: fill.town,
      county: fill.county,
      postcode: fill.postcode,
      countryCode: fill.countryCode,
    }));
    setLookupNote(null);
    setFailure(null);
    setStep("details");
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (step === "find") return;
    setSaving(true);
    setFailure(null);
    try {
      const added = await onSubmit({
        label: draft.label,
        lines: [draft.line1, draft.line2, draft.line3].filter(
          (line, i) => i === 0 || line.trim() !== "",
        ),
        town: draft.town || null,
        county: draft.county || null,
        postcode: draft.postcode,
        country_code: draft.countryCode,
        recipient_name: draft.recipientName || null,
        recipient_phone: draft.recipientPhone || null,
        delivery_instructions: draft.instructions || null,
      });
      onAdded(added);
    } catch (err) {
      setFailure(addressFormFailure(err));
    } finally {
      setSaving(false);
    }
  }

  const field = (
    key: keyof Draft,
    label: string,
    options: {
      name?: AddressFormField;
      autoComplete?: string;
      type?: string;
      hint?: string;
    } = {},
  ) => {
    const error = options.name ? errors[options.name] : undefined;
    const inputId = `${id}-${key}`;
    return (
      <div className="space-y-1.5">
        <Label htmlFor={inputId}>{label}</Label>
        <Input
          id={inputId}
          type={options.type ?? "text"}
          value={draft[key]}
          disabled={saving}
          autoComplete={options.autoComplete ?? "off"}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${inputId}-error` : undefined}
          onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
        />
        {options.hint && !error && (
          <p className="text-xs text-muted-foreground">{options.hint}</p>
        )}
        {error && (
          <p
            id={`${inputId}-error`}
            className="text-sm text-red-700 dark:text-red-400"
          >
            {error}
          </p>
        )}
      </div>
    );
  };

  if (step === "find" && findAddresses) {
    return (
      <form
        onSubmit={(e) => void submit(e)}
        aria-label="Add a delivery address"
        className="space-y-3 rounded-md border p-3"
      >
        <FindAddressByPostcode
          find={findAddresses}
          disabled={saving}
          onChoose={choose}
          onUnavailable={(message) => {
            setLookupNote(message);
            setStep("details");
          }}
          onTypeInstead={() => setStep("details")}
        />
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </form>
    );
  }

  const canFindAnother =
    findAddresses !== null && lookupNote === null && draft.countryCode === "GB";

  return (
    <form
      onSubmit={(e) => void submit(e)}
      aria-label="Add a delivery address"
      className="space-y-3 rounded-md border p-3"
    >
      {lookupNote && (
        <p role="status" className="text-sm text-muted-foreground">
          {lookupNote}
        </p>
      )}
      {canFindAnother && (
        <Button
          type="button"
          variant="link"
          className="h-auto px-0"
          disabled={saving}
          onClick={() => {
            setLookupNote(null);
            setStep("find");
          }}
        >
          Find an address by postcode
        </Button>
      )}
      {failure && (
        <p role="alert" className="text-sm text-red-700 dark:text-red-400">
          {failure.summary}
        </p>
      )}
      {field("label", "Name for this address", {
        name: "label",
        hint: "Your own name for it, like Leeds depot.",
      })}
      {field("line1", "Address line 1", {
        name: "lines",
        autoComplete: "address-line1",
      })}
      {field("line2", "Address line 2 (optional)", {
        autoComplete: "address-line2",
      })}
      {field("line3", "Address line 3 (optional)", {
        autoComplete: "address-line3",
      })}
      <div className="grid gap-3 sm:grid-cols-2">
        {field("town", "Town or city", {
          name: "town",
          autoComplete: "address-level2",
        })}
        {field("county", "County (optional)", {
          name: "county",
          autoComplete: "address-level1",
        })}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {field("postcode", "Postcode", {
          name: "postcode",
          autoComplete: "postal-code",
        })}
        <div className="space-y-1.5">
          <Label htmlFor={`${id}-country`}>Country</Label>
          <select
            id={`${id}-country`}
            value={draft.countryCode}
            disabled={saving}
            autoComplete="country"
            aria-invalid={errors.country_code ? true : undefined}
            aria-describedby={
              errors.country_code ? `${id}-country-error` : undefined
            }
            onChange={(e) =>
              setDraft({ ...draft, countryCode: e.target.value })
            }
            className="border-input bg-input-background focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive h-9 w-full rounded-md border px-3 py-2 text-sm outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {countries.length === 0 ? (
              // The list always arrives with the book; if it somehow does not, the default stays chosen rather than
              // the form sending an empty country.
              <option value={DEFAULT_ADDRESS_COUNTRY_CODE}>
                United Kingdom
              </option>
            ) : (
              countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))
            )}
          </select>
          {errors.country_code && (
            <p
              id={`${id}-country-error`}
              className="text-sm text-red-700 dark:text-red-400"
            >
              {errors.country_code}
            </p>
          )}
        </div>
      </div>
      <fieldset className="space-y-3">
        <legend className="text-sm text-muted-foreground">
          Who receives the goods here (optional)
        </legend>
        <div className="grid gap-3 sm:grid-cols-2">
          {field("recipientName", "Recipient's name", {
            name: "recipient_name",
          })}
          {field("recipientPhone", "Recipient's phone", {
            name: "recipient_phone",
            type: "tel",
          })}
        </div>
        {field("instructions", "Delivery instructions", {
          name: "delivery_instructions",
        })}
      </fieldset>
      <div className="flex items-center gap-2">
        <Button type="submit" disabled={saving}>
          {saving && <Loader2 className="animate-spin" />}
          Save address
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={saving}
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
